/**
 * Pure height-bounding math for the new-session model-picker FAB menu
 * (coach-tooltip-system Phase 2 / Issue 1).
 *
 * The menu (`.sl-fab-menu`) opens *upward* from the FAB (CSS `bottom:60px` of the
 * `.sl-fab-ns-wrap`). Its old `max-height:70vh` was viewport-relative, so inside the
 * Agents drawer (78svh sheet) a growing provider list punched through the drawer
 * header/search. The correct bound is the space ABOVE the FAB, up to the top of the
 * scrollable list (`.sl-list`), which sits below the search + drawer header in flow —
 * so bounding to `listTop` clears both for free, and stays correct when the search
 * expands (list top moves down) and in the legacy non-drawer view.
 *
 * `menuOffsetPx` MUST mirror the CSS `.sl-fab-menu { bottom: 60px }` — they are two
 * faces of one constant. The SE asserts this math directly (deterministic in CI);
 * a companion grep asserts the CSS actually consumes `--coach-fab-available`.
 *
 * @param wrapBottom  viewport-Y of the FAB wrap's bottom edge (getBoundingClientRect().bottom)
 * @param listTop     viewport-Y of the scrollable list's top edge (the boundary to clear)
 * @returns the px value to write into `--coach-fab-available`, floored so the menu is
 *          always at least `floorPx` tall (it scrolls within bounds via overflow-y:auto).
 */
export function computeFabMenuMaxHeightPx(
  wrapBottom: number,
  listTop: number,
  opts: { menuOffsetPx?: number; gutterPx?: number; floorPx?: number } = {},
): number {
  const { menuOffsetPx = 60, gutterPx = 12, floorPx = 120 } = opts;
  const menuBottomY = wrapBottom - menuOffsetPx; // menu's bottom edge sits 60px above the FAB
  const available = menuBottomY - listTop - gutterPx;
  return Math.max(floorPx, Math.round(available));
}
